import React, { useContext, useEffect, useState } from 'react';
import {
  IDisplayReturnItems,
  IMiscellaneousReturnItems,
  IOrderItemDetails,
  IProductDetails,
  IReturnItems,
  IUnifiedBuyerOrder,
  IOrderHistoryDetails,
  IBuyerOrder,
} from '../../model/IOrderHistory';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  AccordionActions,
  Button,
  TextField,
  IconButton,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import { MdOutlineClose, MdLocationOn, MdPeople } from 'react-icons/md';
import Grid from '@mui/material/Grid';
import {
  FaFileAlt,
  FaFileMedical,
  FaSearch,
  FaChevronDown,
  FaTimes,
  FaCaretDown,
} from 'react-icons/fa';
import { Field, LinkFieldValue, RichText, Image } from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import OrderHistoryDropdown from './OrderHistoryDropdown/OrderHistoryDropdown';
import ManageAssignments from '../ManageAssignments/ManageAssignments';
import { FormatOptionsFieldsProps } from '../Product/StandardProductCartDetails/StandardProductCartDetails';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import { addEllipsis, getCurrencyInUSD, getDateTimePartsFromIso } from '../../utils/helper';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import nextId from 'react-id-generator';
import { APIWebstoreContext } from '../../context/globalContext';
import Autocomplete from '@mui/material/Autocomplete';
import {
  ICP_IDENTIFIER,
  STANDARDS_IDENTIFIER,
  TRAINING_IDENTIFIER,
  PURCHASE_ORDER,
} from '../../utils/constants';
import { LOB } from '../CartLOBListDetails/CartLOBListDetails';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { MODALITY_TITLES, ProductType } from '../../utils/ProductTypes';
import DOMPurify from 'dompurify';
import { EnrollmentKeysSection } from './EnrollmentKeySection/EnrollmentKeySection';

export type OrderHistoryProps = {
  fields: {
    AddendaErrataText: Field<string>;
    NewEditionText: Field<string>;
    TrackPackageText: Field<string>;
    ViewReceiptText: Field<string>;
    ViewProFormaInvoiceText: Field<string>;
    ManageAssignmentsText: Field<string>;
    EmailSentHeaderText: Field<string>;
    EmailHeaderText: Field<string>;
    ActionsHeaderText: Field<string>;
    StatusTooltipText: Field<string>;
    ActionTooltipText: Field<string>;
    ResendButtonText: Field<string>;
    ReassignButtonText: Field<string>;
    CancelButtonText: Field<string>;
    SaveAndSendLink: Field<string>;
    EmailErrorMessage: Field<string>;
    FormatOptions: Array<FormatOptionsFieldsProps>;
    DownloadPluginText: Field<string>;
    CreditCard: Field<string>;
    PurchaseOrder: Field<string>;
    OrderHistoryToastMessage?: Field<string>;
    ButtonText?: Field<string>;
    OrderDetailsText: Field<string>;
    DateRefundHeader: Field<string>;
    ViewRefundReceiptText: Field<string>;
    OrderStatusText: Field<string>;
    DomesticTrackingLink: Field<string>;
    InternationalTrackingLink: Field<string>;
    RefundHeaderText: Field<string>;
    PartialRefundStatusText: Field<string>;
    PartialRefundStatusInfoText: Field<string>;
    RefundCompleteText: Field<string>;
    RefundCompleteStatusInfoText: Field<string>;
    GoToStatisticsStoreText: Field<string>;
    ICPRedirectionLink: Field<LinkFieldValue>;
    TrainingRedirectionLink: Field<LinkFieldValue>;
    LearnUponRedirectionLink: Field<LinkFieldValue>;
    LOBs: Array<LOB>;
    ReferenceNumberTooltip: Field<string>;
    OrderNumberTooltip: Field<string>;
    EnrollmentKeyTooltip: Field<string>;
    KeyGenerationFailureMessage: Field<string>;
    CourseEnrollmentFailureMessage: Field<string>;
  };
};

const OrderStatuses = ['Canceled', 'Completed', 'Open', 'AwaitingApproval'];

const OrderHistory = ({ fields }: OrderHistoryProps): React.JSX.Element => {
  const [openManageAssignmentsPopup, setOpenManageAssignmentsPopup] = useState(false);
  const [defaultExpandAccordion, setDefaultExpandAccordion] = useState(false);
  const [showToast, setShowToast] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [orderId, setOrderId] = useState('');
  const [lineItemId, setLineItemId] = useState('');
  const [interval, setInterval] = useState('30days');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedOrder, setSelectedOrder] = useState('');
  const [filterKeyword, setFilterKeyword] = useState('');
  const [orderHistoryDetails, setOrderHistoryDetails] = useState(Array<IOrderHistoryDetails>());
  const [orderHistoryList, setOrderHistoryList] = useState(Array<IOrderHistoryDetails>());
  const [productList, setProductList] = useState(Array<string>());
  const [orderNumberList, setOrderNumberList] = useState(Array<string>());
  const [orderStatuses, setOrderStatuses] = useState(Array<string>());
  const [selectedStatuses, setSelectedStatuses] = useState(Array<string>());
  const { state } = useContext(APIWebstoreContext);
  const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(true);
  const [expanded, setExpanded] = useState<number | false>(false);

  useEffect(() => {
    setOrderStatuses(OrderStatuses);
    getOrderHistoryDetails(interval);
    setIsFirstTimeLoad(false);
  }, []);

  useEffect(() => {
    updateOrderHistoryFilterList();
    isFirstTimeLoad === false && setIsLoading(false);
  }, [orderHistoryDetails]);

  function getOrderHistoryApiUrl(interval: string) {
    let apiUrl = `${process.env.REACT_APP_CART_URL}/cartorder/orderhistory/days/30`;
    let searchInterval = interval;
    if (interval === '6months') {
      searchInterval = 'months';
    } else if (interval === '') {
      searchInterval = 'default';
    } else if (interval !== '30days') {
      searchInterval = 'year';
    }
    switch (searchInterval) {
      case 'year':
        apiUrl = `${process.env.REACT_APP_CART_URL}/cartorder/orderhistory?dateFilter=year&dateFilterValue=${interval}`;
        break;
      case 'months':
        apiUrl = `${process.env.REACT_APP_CART_URL}/cartorder/orderhistory?dateFilter=days&dateFilterValue=180`;
        break;
      default:
        apiUrl = `${process.env.REACT_APP_CART_URL}/cartorder/orderhistory?dateFilter=days&dateFilterValue=30`;
        break;
    }
    return apiUrl;
  }

  const hasUnfulfilledLMSItem = (order: IOrderHistoryDetails) =>
    order.unifiedBuyerOrders.some(
      (buyerOrder) =>
        buyerOrder.lob === TRAINING_IDENTIFIER &&
        buyerOrder.buyerOrders.some(
          (bo) => bo.paymentProcessed && bo.orderItems.some((item) => item.isItemFulfilled === null)
        )
    );

  const pollLMSOrder = (
    unifiedReferenceNumber: string,
    index: number,
    retries = 4,
    interval = 30000
  ) => {
    if (retries <= 0) {
      setOrderHistoryDetails((prev) =>
        prev.map((order, idx) => {
          if (idx !== index) return order;
          order.unifiedBuyerOrders.forEach((bo) =>
            bo.buyerOrders.forEach((buyerOrder) =>
              buyerOrder.orderItems.forEach((item) => (item.isItemFulfilled ??= false))
            )
          );
          return order;
        })
      );
      return;
    }

    const apiUrl = `${process.env.REACT_APP_CART_URL}/cartorder/orderhistory?unifiedreferencenumber=${unifiedReferenceNumber}`;

    getAPICall(apiUrl)
      .then((response) => {
        if (!response || response.status !== 200) return;

        const updatedOrder: IOrderHistoryDetails = response.data[0];

        setOrderHistoryDetails((prev) =>
          prev.map((order, idx) => (idx === index ? updatedOrder : order))
        );

        const itemUnfulfilled = hasUnfulfilledLMSItem(updatedOrder);

        if (itemUnfulfilled) {
          setTimeout(
            () => pollLMSOrder(unifiedReferenceNumber, index, retries - 1, interval),
            interval
          );
        }
      })
      .catch(() => {
        setTimeout(
          () => pollLMSOrder(unifiedReferenceNumber, index, retries - 1, interval),
          interval
        );
      });
  };

  function getOrderHistoryDetails(interval: string) {
    let apiUrl = getOrderHistoryApiUrl(interval);

    getAPICall(apiUrl).then(function (response: any) {
      if (response && response.status === 200) {
        let orderHistoryData: IOrderHistoryDetails[] = response.data;

        setDefaultExpandAccordion(false);
        setOrderHistoryDetails(orderHistoryData);
        setOrderHistoryList(orderHistoryData);

        orderHistoryData.forEach((unifiedOrder, index) => {
          if (hasUnfulfilledLMSItem(unifiedOrder) && unifiedOrder.unifiedReferenceNumber) {
            pollLMSOrder(unifiedOrder.unifiedReferenceNumber, index);
          }
        });
      }
    });
  }

  const updateOrderHistoryFilterList = () => {
    let productList: string[] = [];
    let orderNumberList: string[] = [];
    orderHistoryDetails.forEach((orderHistory) => {
      orderHistory.unifiedBuyerOrders.forEach((buyerOrder) => {
        buyerOrder.buyerOrders.forEach((orderItem) => {
          if (orderItem?.orderId && !orderNumberList.includes(orderItem.orderId)) {
            orderNumberList.push(orderItem.orderId);
          }

          orderItem.orderItems.forEach((orderProduct) => {
            const title = orderProduct?.product?.title || '';
            if (title && !productList.includes(title)) {
              productList.push(title);
            }
          });
        });
      });
    });
    setOrderNumberList(orderNumberList);
    setProductList(productList);
  };

  const onOpenManageAssignmentsPopup = (orderId: string, lineItemId: string) => {
    setOpenManageAssignmentsPopup(true);
    setOrderId(orderId);
    setLineItemId(lineItemId);
  };

  const onCloseManageAssignmentsPopup = () => {
    setOpenManageAssignmentsPopup(false);
    setOrderId('');
    setLineItemId('');
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  const setToggleAccordion = (id: number, expand: boolean) => {
    if (id >= 0) {
      let element: any = document.getElementById(id.toString());
      element.expanded = expand;
    } else {
      let elements: any = document.getElementsByClassName('accordionOrderHistory');
      let i = elements.length - 1;
      while (i >= 0) {
        elements[i].expanded = expand;
        i--;
      }
    }
  };

  const filterOrderHistory = (
    filterStatusBy: string[],
    filterOrderBy: string,
    filterProductBy: string
  ) => {
    setFilterKeyword('');
    let filteredRes = filterByStatus([...orderHistoryList], filterStatusBy);
    filteredRes = filterByOrderNumber(filteredRes, filterOrderBy);
    filteredRes = filterByProductTitle(filteredRes, filterProductBy);
    setOrderHistoryDetails([...filteredRes]);
  };

  const filterByStatus = (
    filteredOrderHistoryList: IOrderHistoryDetails[],
    filterStatusBy: string[]
  ) => {
    if (filterStatusBy.length === 0) {
      return filteredOrderHistoryList;
    }

    const statuses = filterStatusBy.map((status) => status.toLocaleLowerCase());

    const filteredResult = filteredOrderHistoryList.filter((orderHistory) =>
      orderHistory.unifiedBuyerOrders.some((buyerOrder) =>
        buyerOrder.buyerOrders.some((order) => {
          const orderStatus = order.status ? order.status.toLocaleLowerCase() : '';
          return statuses.includes(orderStatus);
        })
      )
    );

    return filteredResult;
  };

  const filterByOrderNumber = (
    filteredOrderHistoryList: IOrderHistoryDetails[],
    filterOrderNumberBy: string
  ) => {
    if (!filterOrderNumberBy || filterOrderNumberBy.length === 0) {
      return filteredOrderHistoryList;
    }
    const filteredResult = filteredOrderHistoryList.filter((orderHistory) =>
      orderHistory.unifiedBuyerOrders.some((buyerOrder) =>
        buyerOrder.buyerOrders.some(
          (order) => order.orderId.toLocaleLowerCase() === filterOrderNumberBy.toLocaleLowerCase()
        )
      )
    );
    return filteredResult;
  };

  const filterByProductTitle = (
    filteredOrderHistoryList: IOrderHistoryDetails[],
    filterProductBy: string
  ) => {
    if (!filterProductBy || filterProductBy.length === 0) {
      return filteredOrderHistoryList;
    }
    const filteredArray = filteredOrderHistoryList.filter((orderHistory) =>
      orderHistory.unifiedBuyerOrders.some((buyerOrder) =>
        buyerOrder.buyerOrders.some((order) =>
          order.orderItems.some(
            (item) =>
              item?.product?.title.toLocaleLowerCase().replace(/\s+/g, '') ===
              filterProductBy.toLocaleLowerCase().replace(/\s+/g, '')
          )
        )
      )
    );
    return filteredArray;
  };

  const showClearFilterLink = () => {
    return (selectedOrder && selectedOrder.length > 0) ||
      (selectedProduct && selectedProduct.length > 0) ||
      selectedStatuses.length > 0
      ? true
      : false;
  };

  const handleClearAllFilter = () => {
    setSelectedOrder('');
    setSelectedProduct('');
    setSelectedStatuses([]);
    setFilterKeyword('');
    setIsLoading(true);
    setOrderHistoryDetails([...orderHistoryList]);
  };

  const clearFilterKey = () => {
    setIsLoading(true);
    setFilterKeyword('');
    filterByKeyword('');
  };

  const handleFilterSearch = (e: any) => {
    if (!e.key || e.key === 'Enter') {
      setIsLoading(true);
      filterByKeyword(filterKeyword);
    }
  };

  const filterByKeyword = (filterKeyword: string) => {
    let filteredRes = filterByStatus([...orderHistoryList], selectedStatuses);
    filteredRes = filterByOrderNumber(filteredRes, selectedOrder);
    filteredRes = filterByProductTitle(filteredRes, selectedProduct);
    if (filterKeyword.length === 0) {
      setOrderHistoryDetails([...filteredRes]);
      return;
    }

    const filteredResultsByOrder = filteredRes.filter(
      (orderHistory) =>
        orderHistory.unifiedReferenceNumber
          ?.toLocaleLowerCase()
          .includes(filterKeyword.toLocaleLowerCase()) ||
        orderHistory.unifiedBuyerOrders.some((buyerOrder) =>
          buyerOrder.buyerOrders.some((order) => {
            const orderIdMatch = order.orderId
              .toLocaleLowerCase()
              .includes(filterKeyword.toLocaleLowerCase());
            const statusMatch = order.status
              .toLocaleLowerCase()
              .includes(filterKeyword.toLocaleLowerCase());
            const totalMatch = order.total.toString().includes(filterKeyword.toLocaleLowerCase());

            const enrollmentKeyMatch = order.orderItems?.some((item) =>
              item.enrollmentKey?.toLocaleLowerCase().includes(filterKeyword.toLocaleLowerCase())
            );

            return orderIdMatch || statusMatch || totalMatch || enrollmentKeyMatch;
          })
        )
    );

    let filteredResByProducts = filterProductsByKeyword([...filteredRes]);
    let filteredResult = filteredResultsByOrder.concat(filteredResByProducts);
    let result = filteredResult.filter(
      (result, index) =>
        index ===
        filteredResult.findIndex(
          (res) => res.unifiedReferenceNumber === result.unifiedReferenceNumber
        )
    );
    setOrderHistoryDetails([...result]);
  };

  const filterProductsByKeyword = (filteredOrderHistoryList: IOrderHistoryDetails[]) => {
    let filterKey = filterKeyword.toLocaleLowerCase().replace(/\s+/g, '');

    let filteredArray = filteredOrderHistoryList.filter((orderHistory) =>
      orderHistory.unifiedBuyerOrders.some((buyerOrder) =>
        buyerOrder.buyerOrders.some((order) =>
          order.orderItems.some((item) => {
            const productTitle =
              item.product?.title?.toLocaleLowerCase().replace(/\s+/g, '').includes(filterKey) ||
              '';
            const productId =
              item.product?.productId
                ?.toLocaleLowerCase()
                .replace(/\s+/g, '')
                .includes(filterKey) || '';
            const format =
              item.format?.toLocaleLowerCase().replace(/\s+/g, '').includes(filterKey) || '';
            const shipToCountry =
              item.shipToCountry?.toLocaleLowerCase().replace(/\s+/g, '').includes(filterKey) || '';
            const language =
              item.language?.toLocaleLowerCase().replace(/\s+/g, '').includes(filterKey) || '';

            return productTitle || productId || format || shipToCountry || language;
          })
        )
      )
    );
    return filteredArray;
  };

  const handleStatusFilterChange = (e: any) => {
    let array = e.target.value;
    setSelectedStatuses([...array]);
    setIsLoading(true);
    filterOrderHistory(array, selectedOrder, selectedProduct);
  };

  const renderSelectedStatus = (selected: string[]) => {
    if (selected.length === 0) {
      return <span className="status-label">Status</span>;
    }
    let selectedStatusArray = selected.map((status) => {
      return state?.sitecoreDictionaryData &&
        state?.sitecoreDictionaryData[status] &&
        state?.sitecoreDictionaryData[status].length > 0
        ? state?.sitecoreDictionaryData[status]
        : status === 'Open'
        ? 'Pending'
        : status;
    });
    let statusFilter =
      selectedStatusArray.length > 1
        ? `${selectedStatusArray[0]} (+${selectedStatusArray.length - 1})`
        : selectedStatusArray[0];
    return statusFilter;
  };

  const handleOrderNumberChange = (orderNumber: string) => {
    setSelectedOrder(orderNumber);
    setIsLoading(true);
    filterOrderHistory(selectedStatuses, orderNumber, selectedProduct);
  };

  const handleProductChange = (product: string) => {
    setSelectedProduct(product);
    setIsLoading(true);
    filterOrderHistory(selectedStatuses, selectedOrder, product);
  };

  function updateSearchInterval(value: string) {
    setIsLoading(true);
    setInterval(value);
    setOrderNumberList([]);
    setProductList([]);
    setFilterKeyword('');
    setSelectedOrder('');
    setSelectedProduct('');
    setSelectedStatuses([]);
    getOrderHistoryDetails(value);
  }

  function getViewReceiptOrInvoiceLink(
    paymentType: string,
    paymentProcessed: boolean,
    orderId: string,
    orderReturned: boolean,
    isNoCostOrder: boolean
  ) {
    if (paymentType === fields.PurchaseOrder?.value && paymentProcessed === false) {
      return (
        <Link
          to={`/orders/receipt?orderid=${orderId}`}
          target="_blank"
          className={!orderReturned ? 'viewReceiptLink' : 'viewReceipt'}
          key={nextId()}
        >
          {!orderReturned && 'View '}
          {fields?.ViewProFormaInvoiceText?.value}
        </Link>
      );
    } else if (
      (paymentType === fields.PurchaseOrder?.value && paymentProcessed === true) ||
      paymentType === fields.CreditCard?.value ||
      isNoCostOrder === true
    ) {
      return (
        <Link
          to={`/orders/receipt?orderid=${orderId}`}
          target="_blank"
          className={!orderReturned ? 'viewReceiptLink' : 'viewReceipt'}
          key={nextId()}
        >
          {!orderReturned && 'View '}
          {fields?.ViewReceiptText?.value}
        </Link>
      );
    } else {
      return;
    }
  }

  function convertDate(date: string | null) {
    if (!date) {
      return '';
    }
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function getProductUrl(productDetails: IProductDetails | null) {
    const modality = productDetails?.modality?.toLowerCase();

    if (modality === ProductType.InstructorLedCourse.toLowerCase()) {
      return `/instructor-led-courses/${productDetails?.productId}`;
    }

    if (modality === ProductType.SelfPacedCourse.toLowerCase()) {
      return `/self-paced-courses/${productDetails?.productId}`;
    }

    let queryString;
    let productType;
    if (productDetails?.isStatisticsReport === true) {
      productType = 'statisticsreports';
      queryString = `?reportYear=${productDetails.year}&&term=${productDetails.term}`;
    } else {
      queryString = productDetails?.isRecent ? '' : `?edition=${productDetails?.edition}`;
      if (productDetails?.isDatasheet) {
        productType = 'datasheets';
      } else {
        productType = 'standards';
      }
    }
    return `/${productType}/${productDetails?.productId}${queryString}`;
  }

  function getOrderHistorySkeleton() {
    return (
      <>
        <Accordion
          defaultExpanded={false}
          className="accordionOrderHistory order-history-loading-div"
        >
          <AccordionSummary
            key={nextId()}
            expandIcon={
              <div key={nextId()}>
                <div key={nextId()}>
                  <ExpandMoreIcon className="expandAccordion" key={nextId()} />
                </div>
                <div key={nextId()}>
                  <MdOutlineClose className="closeAccordion" key={nextId()} />
                </div>
              </div>
            }
            aria-controls="panel1c-content"
            id="panel1c-header"
            className="accordionHeader"
          >
            <Grid container alignItems="stretch" className="OrderHistoryMainContainer">
              <Grid item xs={12} lg={8} spacing={0} container className="OrderHeaderLeftContainer">
                <Grid item xs={12} lg sm={6} md={4} className="LeftGridItem">
                  <div className="accordionColumn" key={nextId()}>
                    <Typography className="accordionHeading" key={nextId()}>
                      Date Ordered
                    </Typography>
                    <Typography className="accordionHeadingValue" key={nextId()}>
                      <LoadingSkeleton theme="light" />
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} lg sm={6} md={4} className="LeftGridItem">
                  <div className="accordionColumn" key={nextId()}>
                    <Typography className="accordionHeading" key={nextId()}>
                      Invoice Number
                    </Typography>
                    <Typography className="accordionHeadingValue" key={nextId()}>
                      <LoadingSkeleton theme="light" />
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} lg sm={6} md={4} className="LeftGridItem">
                  <div className="accordionColumn" key={nextId()}>
                    <Typography className="accordionHeading" key={nextId()}>
                      Order Total
                    </Typography>
                    <Typography className="accordionHeadingValue" key={nextId()}>
                      <LoadingSkeleton theme="light" />
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} lg sm={6} md={4} className="LeftGridItem">
                  <div className="accordionColumn" key={nextId()}>
                    <Typography className="accordionHeading" key={nextId()}>
                      {fields.OrderStatusText?.value}
                    </Typography>
                    <Typography className="accordionHeadingValue" key={nextId()}>
                      <LoadingSkeleton theme="light" />
                    </Typography>
                  </div>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={4} className="OrderHeaderRightContainer">
                <LoadingSkeleton theme="light" />
              </Grid>
            </Grid>
          </AccordionSummary>
        </Accordion>
      </>
    );
  }

  function getPartialRefundStatusForLineItem(returnItems: Array<IReturnItems>, quantity: number) {
    let refundStatusText = fields.PartialRefundStatusText?.value;
    return (
      <Grid item xs={10} sm={6} md={6} lg={8} key={nextId()}>
        <Typography className="refund-header" key={nextId()}>
          {fields.RefundHeaderText?.value}
        </Typography>
        {returnItems.length === 1 && returnItems[0].quantity === quantity ? (
          <div key={nextId()}>
            <Typography className="partial-refund-header" key={nextId()}>
              {refundStatusText.replace('Partial', '')}
            </Typography>
            <Typography className="partial-refund-status" key={nextId()}>
              {fields.RefundCompleteStatusInfoText?.value}
            </Typography>
          </div>
        ) : (
          returnItems.map((returnItem: IReturnItems, id: number) => (
            <div key={nextId()}>
              <Typography className="partial-refund-header" key={nextId()}>
                {fields.PartialRefundStatusText?.value}
              </Typography>
              <Typography className="partial-refund-status" key={nextId()}>
                Your return of <b>{returnItem.quantity}</b> out of <b>{quantity}</b> is complete.
                Your refund has been issued.
              </Typography>
            </div>
          ))
        )}
      </Grid>
    );
  }

  function showTrackPackageButton(item: IBuyerOrder, orderDetails: IOrderItemDetails) {
    let show = false;
    if (
      item.trackingNumber &&
      orderDetails?.isShipmentItem &&
      orderDetails?.itemShipped &&
      !item.isOrderReturned
    ) {
      if (!item.isPartiallyReturned) {
        show = true;
      } else {
        let totalReturnedQuantity = 0;
        orderDetails.returnItems &&
          orderDetails.returnItems.length > 0 &&
          orderDetails.returnItems.forEach(
            (returItem) => (totalReturnedQuantity += returItem.quantity)
          );
        if (totalReturnedQuantity < orderDetails.quantity) {
          show = true;
        } else {
          show = false;
        }
      }
    }
    if (show) {
      return (
        <a
          href={
            state?.sitecoreDictionaryData && orderDetails?.shipToCountry
              ? state?.sitecoreDictionaryData[orderDetails.shipToCountry]
                ? fields.DomesticTrackingLink?.value.replace(
                    '##TRACKING_NUMBER##',
                    item.trackingNumber || ''
                  )
                : fields.InternationalTrackingLink?.value.replace(
                    '##TRACKING_NUMBER##',
                    item.trackingNumber || ''
                  )
              : ''
          }
          target="_blank"
          rel="noreferrer"
          className="buttonBorderBlue"
          key={nextId()}
        >
          <MdLocationOn className="trackPackageIcon" key={nextId()} />
          {fields?.TrackPackageText.value}
        </a>
      );
    } else {
      return;
    }
  }

  function showManageAssignmentsButton(item: IBuyerOrder, orderDetails: IOrderItemDetails) {
    let show = false;
    if (
      orderDetails?.isDigitalItem &&
      (item.paymentProcessed || item.isNoCostOrder) &&
      !item.isOrderReturned &&
      item.isRIKOrderCreated
    ) {
      if (!item.isPartiallyReturned) {
        show = true;
      } else {
        let totalReturnedQuantity = 0;
        orderDetails.returnItems &&
          orderDetails.returnItems.length > 0 &&
          orderDetails.returnItems.forEach(
            (returnItem) => (totalReturnedQuantity += returnItem.quantity)
          );
        if (totalReturnedQuantity < orderDetails.quantity) {
          show = true;
        } else {
          show = false;
        }
      }
    }
    if (show && !orderDetails?.product?.isStatisticsReport) {
      return (
        <Button
          color="secondary"
          variant="contained"
          className="buttonFillBlue"
          onClick={() => onOpenManageAssignmentsPopup(item.orderId, orderDetails.lineItemId)}
          key={nextId()}
        >
          <MdPeople key={nextId()} />
          &nbsp;
          {fields?.ManageAssignmentsText.value}
        </Button>
      );
    } else {
      return;
    }
  }

  function showReceipts(orderHistory: IBuyerOrder) {
    let returnItems: Array<IDisplayReturnItems> = [];

    if (orderHistory.isOrderReturned) {
      returnItems.push({
        orderId: orderHistory.orderId,
        returnId: '',
        receiptText: `${fields.ViewRefundReceiptText?.value}`,
      });
    }

    if (orderHistory.orderItems && orderHistory.orderItems.length > 0) {
      orderHistory.orderItems.forEach(
        (orderDetails: IOrderItemDetails) =>
          orderDetails.returnItems &&
          orderDetails.returnItems.length > 0 &&
          orderDetails.returnItems.forEach((returnItem: IReturnItems) => {
            if (
              !returnItems.some((y) => JSON.stringify(y.returnId) === JSON.stringify(returnItem.id))
            ) {
              returnItems.push({
                orderId: orderHistory.orderId,
                returnId: returnItem.id,
                receiptText: `Return Receipt -(${returnItem.id})`,
              });
            }
          })
      );
    }

    if (
      orderHistory.shippingRefunded &&
      orderHistory.shippingReturnId &&
      orderHistory.shippingReturnId.length > 0
    ) {
      returnItems.push({
        orderId: orderHistory.orderId,
        returnId: orderHistory.shippingReturnId,
        receiptText: `Shipping Return Receipt -(${orderHistory.shippingReturnId})`,
      });
    }

    if (
      orderHistory.taxRefunded &&
      orderHistory.taxReturnId &&
      orderHistory.taxReturnId.length > 0
    ) {
      returnItems.push({
        orderId: orderHistory.orderId,
        returnId: orderHistory.taxReturnId,
        receiptText: `Tax Return Receipt -(${orderHistory.taxReturnId})`,
      });
    }

    if (orderHistory.miscellaneousReturnItems && orderHistory.miscellaneousReturnItems.length > 0) {
      orderHistory.miscellaneousReturnItems.map((returnItem: IMiscellaneousReturnItems) => {
        returnItems.push({
          orderId: orderHistory.orderId,
          returnId: returnItem.id,
          receiptText:
            returnItem.refundType === 'MembershipDiscount'
              ? `Promotional Discount Return Receipt -(${returnItem.id})`
              : `Miscellaneous Return Receipt -(${returnItem.id})`,
        });
      });
    }

    if (returnItems.length === 0 || returnItems.length === null) {
      return (
        <>
          {getViewReceiptOrInvoiceLink(
            orderHistory.paymentType,
            orderHistory.paymentProcessed,
            orderHistory.orderId,
            orderHistory.isOrderReturned,
            orderHistory.isNoCostOrder
          )}
        </>
      );
    }

    if (returnItems.length > 0) {
      returnItems.sort((a, b) => (a.returnId > b.returnId ? 1 : -1));
      return (
        <>
          <div className="dropdown">
            <button className="dropbtn">
              View Receipts
              <FaChevronDown />
            </button>
            <div className="dropdown-content">
              {getViewReceiptOrInvoiceLink(
                orderHistory.paymentType,
                orderHistory.paymentProcessed,
                orderHistory.orderId,
                orderHistory.isOrderReturned,
                orderHistory.isNoCostOrder
              )}

              {returnItems.length > 0 &&
                returnItems.map((returnItems: IDisplayReturnItems) => (
                  <div key={nextId()}>
                    {returnItems.returnId ? (
                      <Link
                        to={{
                          pathname: '/orders/return-receipt',
                          search: `?orderid=${returnItems.orderId}&refund=true&orderReturnId=${returnItems.returnId}`,
                        }}
                        className="viewReceipt"
                        target="_blank"
                        key={nextId()}
                      >
                        {returnItems.receiptText}
                      </Link>
                    ) : (
                      <Link
                        to={{
                          pathname: '/orders/receipt',
                          search: `?orderid=${returnItems.orderId}&refund=true`,
                        }}
                        className="viewReceipt"
                        target="_blank"
                        key={nextId()}
                      >
                        {returnItems.receiptText}
                      </Link>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </>
      );
    }
    return <></>;
  }

  function showUnifiedOrderReceipt(orderHistory: IOrderHistoryDetails) {
    let link = '';

    let showProFormaInvoice = false;

    orderHistory.unifiedBuyerOrders.forEach((unifiedBuyerOrder) => {
      unifiedBuyerOrder.buyerOrders.forEach((buyerOrder) => {
        if (buyerOrder.paymentType === PURCHASE_ORDER && buyerOrder.paymentProcessed === false) {
          showProFormaInvoice = true;
        }
      });
    });

    orderHistory.unifiedBuyerOrders.forEach((buyerOrder) => {
      buyerOrder.buyerOrders.forEach((order) => {
        if (orderHistory.unifiedReferenceNumber !== null) {
          link = orderHistory.unifiedReferenceNumber;
        }
      });
    });

    return (
      <>
        <Link
          to={`/orders/unifiedreceipt?referenceNo=${link}`}
          target="_blank"
          className="viewReceiptLink"
          key={nextId()}
        >
          {showProFormaInvoice ? (
            <>
              {'View '}
              {fields?.ViewProFormaInvoiceText?.value}
            </>
          ) : (
            <>
              {'View '}
              {fields?.ViewReceiptText?.value}
            </>
          )}
        </Link>
      </>
    );
  }

  function renderOrderHistoryFilter() {
    return (
      <>
        <Grid container alignItems="stretch" className="filter-content">
          <Grid
            item
            xs={12}
            lg={2}
            sm={6}
            md={6}
            container
            sx={{ p: 0.5 }}
            className="interval-dropdown-div"
          >
            <OrderHistoryDropdown disabled={isLoading} onIntervalChange={updateSearchInterval} />
          </Grid>

          <Grid item xs={12} lg={2} sm={6} md={6} container sx={{ p: 0.5 }}>
            <Select
              className="filter-multiselect"
              disableUnderline
              IconComponent={FaCaretDown}
              multiple
              value={selectedStatuses}
              renderValue={(selected: any) => renderSelectedStatus(selected)}
              onChange={(e) => handleStatusFilterChange(e)}
              displayEmpty
              disabled={isLoading}
              sx={{
                '& .MuiSelect-select': {
                  padding: '0.3em',
                },
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
            >
              <MenuItem key="default" value="Status" disabled className="status-select-menu">
                <ListItemText primary="---Status---" />
              </MenuItem>

              {orderStatuses.map((status) => (
                <MenuItem key={status} value={status} className="status-select-menu">
                  <Checkbox
                    checked={selectedStatuses.indexOf(status) > -1}
                    className="menu-checkbox"
                  />
                  <ListItemText
                    primary={
                      state?.sitecoreDictionaryData &&
                      state?.sitecoreDictionaryData[status] &&
                      state?.sitecoreDictionaryData[status].length > 0
                        ? state?.sitecoreDictionaryData[status]
                        : status === 'Open'
                        ? 'Pending'
                        : status
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} lg={2} sm={6} md={6} container sx={{ p: 0.5 }}>
            <Autocomplete<string, false, true, false>
              id="order-number-filter"
              options={orderNumberList}
              className="filter-autocomplete"
              onChange={(_, value) => handleOrderNumberChange(value ?? '')}
              value={selectedOrder ?? ''}
              isOptionEqualToValue={(option, value) => option === value}
              disableClearable
              disabled={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Invoice Number"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} lg={2} sm={6} md={6} container sx={{ p: 0.5 }}>
            <Autocomplete<string, false, true, false>
              id="product-title-filter"
              className="filter-autocomplete"
              options={productList}
              onChange={(_, value) => handleProductChange(value ?? '')}
              value={selectedProduct ?? ''}
              disableClearable
              disabled={isLoading}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Product Title"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                />
              )}
            />
          </Grid>

          <Grid item container sx={{ p: 0.5, flex: 1 }}>
            <div className="search-keyword">
              <div className="input-div">
                <input
                  className="input-search"
                  placeholder="Filter By Keyword"
                  onChange={(e) => setFilterKeyword(e.target.value)}
                  value={filterKeyword}
                  onKeyUp={(e) => handleFilterSearch(e)}
                />
              </div>

              <div className="icons-div">
                <IconButton
                  className={
                    !filterKeyword || filterKeyword.length === 0
                      ? 'filter-search show-filter-clear'
                      : 'filter-search'
                  }
                  onClick={clearFilterKey}
                >
                  <FaTimes className="searchicon" />
                </IconButton>

                <IconButton className="filter-search" onClick={handleFilterSearch}>
                  <FaSearch className="searchicon" />
                </IconButton>
              </div>
            </div>
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <div className="orderhistory">
      <div className="orderhistoryheader">
        <div className="orderhistorydiv">
          <h1 className="orderHisoryHeading">Order History</h1>
        </div>
        <div className="clear-filter-div">
          {showClearFilterLink() ? <a onClick={handleClearAllFilter}>Clear Filter</a> : <></>}
        </div>
      </div>
      <div>{renderOrderHistoryFilter()}</div>
      {isLoading ? (
        getOrderHistorySkeleton()
      ) : (
        <>
          <div className="order-history-details-div">
            <div className="downloadplugin">
              <RichText field={fields.DownloadPluginText} />
            </div>
            {orderHistoryDetails && orderHistoryDetails.length > 0 ? (
              orderHistoryDetails.map((unifiedOrder: IOrderHistoryDetails, id: number) => {
                const hideAccordionSummary = unifiedOrder.unifiedBuyerOrders.some((buyerOrder) =>
                  buyerOrder.buyerOrders.some(
                    (order) => order.orderId === unifiedOrder.unifiedReferenceNumber
                  )
                );
                return (
                  unifiedOrder.unifiedBuyerOrders.length > 0 && (
                    <div key={nextId()}>
                      <Accordion
                        defaultExpanded={defaultExpandAccordion}
                        className="accordionOrderHistory"
                        id={id.toString()}
                        key={unifiedOrder.unifiedReferenceNumber}
                      >
                        {!hideAccordionSummary && (
                          <AccordionSummary
                            key={nextId()}
                            expandIcon={
                              <div key={nextId()}>
                                <div onClick={() => setToggleAccordion(id, true)} key={nextId()}>
                                  <ExpandMoreIcon className="expandAccordion" key={nextId()} />
                                </div>
                                <div onClick={() => setToggleAccordion(id, false)} key={nextId()}>
                                  <MdOutlineClose className="closeAccordion" key={nextId()} />
                                </div>
                              </div>
                            }
                            aria-controls="panel1c-content"
                            id="panel1c-header"
                            className="accordionHeader"
                          >
                            <Grid
                              container
                              alignItems="stretch"
                              className="OrderHistoryMainContainer"
                            >
                              <Grid
                                item
                                xs={12}
                                lg={8}
                                spacing={0}
                                container
                                className="OrderHeaderLeftContainer"
                              >
                                <Grid item xs={12} lg sm={6} md className="LeftGridItem">
                                  <div className="accordionColumn" key={nextId()}>
                                    <Typography className="accordionHeading" key={nextId()}>
                                      {state?.sitecoreDictionaryData?.DateOrdered || 'Date Ordered'}
                                    </Typography>
                                    <Typography className="accordionHeadingValue" key={nextId()}>
                                      {convertDate(unifiedOrder.dateOrdered)}
                                    </Typography>
                                  </div>
                                </Grid>

                                <Grid item xs={12} lg sm={6} md className="LeftGridItem">
                                  <div className="accordionColumn" key={nextId()}>
                                    <Typography className="accordionHeading" key={nextId()}>
                                      {unifiedOrder.unifiedReferenceNumber
                                        ? 'Reference Number'
                                        : (() => {
                                            let invoiceLabel = 'Invoice Number';

                                            unifiedOrder.unifiedBuyerOrders.forEach(
                                              (buyerOrder) => {
                                                buyerOrder.buyerOrders.forEach((order) => {
                                                  if (order.orderId) {
                                                    invoiceLabel = 'Invoice Number';
                                                    return;
                                                  }
                                                });
                                              }
                                            );

                                            return invoiceLabel;
                                          })()}
                                      {unifiedOrder.unifiedReferenceNumber &&
                                        fields?.ReferenceNumberTooltip && (
                                          <CustomTooltip
                                            fields={{
                                              'Tooltip Text': fields.ReferenceNumberTooltip,
                                            }}
                                          />
                                        )}
                                      {!unifiedOrder.unifiedReferenceNumber &&
                                        fields.OrderNumberTooltip && (
                                          <CustomTooltip
                                            fields={{ 'Tooltip Text': fields.OrderNumberTooltip }}
                                          />
                                        )}
                                    </Typography>
                                    <Typography className="accordionHeadingValue" key={nextId()}>
                                      {unifiedOrder.unifiedReferenceNumber ||
                                        unifiedOrder.unifiedBuyerOrders.map((buyerOrder) =>
                                          buyerOrder.buyerOrders.map((order) => order.orderId)
                                        )}
                                    </Typography>
                                  </div>
                                </Grid>
                                <Grid item xs={12} lg sm={6} md className="LeftGridItem">
                                  <div className="accordionColumn" key={nextId()}>
                                    <Typography className="accordionHeading" key={nextId()}>
                                      Order Total
                                    </Typography>
                                    <Typography className="accordionHeadingValue" key={nextId()}>
                                      {getCurrencyInUSD(unifiedOrder.total)}
                                    </Typography>
                                  </div>
                                </Grid>
                                {unifiedOrder && unifiedOrder.unifiedReferenceNumber === null && (
                                  <Grid item xs={12} lg sm={6} md className="LeftGridItem">
                                    <div className="accordionColumn" key={nextId()}>
                                      <Typography className="accordionHeading" key={nextId()}>
                                        {fields.OrderStatusText?.value}
                                      </Typography>
                                      <Typography className="accordionHeadingValue" key={nextId()}>
                                        {unifiedOrder.unifiedBuyerOrders.map((buyerOrder) =>
                                          buyerOrder.buyerOrders.map((order) => {
                                            const orderStatus =
                                              state?.sitecoreDictionaryData?.[order.status];
                                            return orderStatus || '';
                                          })
                                        )}
                                      </Typography>
                                    </div>
                                  </Grid>
                                )}
                              </Grid>

                              <Grid item xs={12} lg={4} className="OrderHeaderRightContainer">
                                {unifiedOrder?.unifiedReferenceNumber === null
                                  ? unifiedOrder.unifiedBuyerOrders.map((buyerOrder) =>
                                      buyerOrder.buyerOrders.map((order) => showReceipts(order))
                                    )
                                  : showUnifiedOrderReceipt(unifiedOrder)}
                              </Grid>
                            </Grid>
                          </AccordionSummary>
                        )}
                        <AccordionDetails className="accordionDetails" key={nextId()}>
                          <div className="accordionColumn accordionBodySection" key={nextId()}>
                            {unifiedOrder.unifiedBuyerOrders?.length > 0 ? (
                              <>
                                {unifiedOrder.unifiedBuyerOrders.map(
                                  (buyerOrder) =>
                                    buyerOrder.buyerOrders.length > 0 && (
                                      <div
                                        key={nextId()}
                                        className={`section-${buyerOrder.lob.toLowerCase()} sectionArea 
                                                  ${
                                                    hideAccordionSummary
                                                      ? 'singleInvoiceSection'
                                                      : ''
                                                  }`}
                                      >
                                        {buyerOrder.buyerOrders.map((order) => (
                                          <div key={nextId()}>
                                            <div className="accordionHeader">
                                              <Grid
                                                container
                                                alignItems="stretch"
                                                className="accImgContainer"
                                              >
                                                {fields.LOBs.map((lob) =>
                                                  lob.fields.Id.value.toLowerCase() ===
                                                  buyerOrder.lob.toLowerCase() ? (
                                                    <Image
                                                      key={nextId()}
                                                      field={lob.fields.DesktopImage.value}
                                                      className="section-image"
                                                      style={{
                                                        width: `${lob.fields?.ImageWidth?.value}px`,
                                                        height: `${lob.fields?.ImageHeight?.value}px`,
                                                      }}
                                                    />
                                                  ) : (
                                                    <></>
                                                  )
                                                )}
                                              </Grid>
                                              <Grid
                                                container
                                                alignItems="stretch"
                                                className="OrderHistoryMainContainer"
                                              >
                                                <Grid
                                                  item
                                                  xs={12}
                                                  lg={8}
                                                  spacing={0}
                                                  container
                                                  className="OrderHeaderLeftContainer"
                                                >
                                                  <Grid
                                                    item
                                                    xs={12}
                                                    lg
                                                    sm={6}
                                                    md
                                                    className="LeftGridItem"
                                                    key={nextId()}
                                                  >
                                                    <div className="accordionColumn" key={nextId()}>
                                                      <Typography
                                                        className="accordionHeading"
                                                        key={nextId()}
                                                      >
                                                        {state?.sitecoreDictionaryData
                                                          ?.DateOrdered || 'Date Ordered'}
                                                      </Typography>
                                                      <Typography
                                                        className="accordionHeadingValue"
                                                        key={nextId()}
                                                      >
                                                        {convertDate(order.dateOrdered)}
                                                      </Typography>
                                                    </div>
                                                  </Grid>

                                                  <Grid
                                                    item
                                                    xs={12}
                                                    lg
                                                    sm={6}
                                                    md
                                                    className="LeftGridItem"
                                                  >
                                                    <div className="accordionColumn" key={nextId()}>
                                                      <Typography
                                                        className="accordionHeading"
                                                        key={nextId()}
                                                      >
                                                        {state?.sitecoreDictionaryData
                                                          ?.OrderNumber || 'Invoice Number'}
                                                        <CustomTooltip
                                                          fields={{
                                                            'Tooltip Text':
                                                              fields.OrderNumberTooltip,
                                                          }}
                                                        />
                                                      </Typography>
                                                      <Typography
                                                        className="accordionHeadingValue"
                                                        key={nextId()}
                                                      >
                                                        {order.orderId}
                                                      </Typography>
                                                    </div>
                                                  </Grid>

                                                  <Grid
                                                    item
                                                    xs={12}
                                                    lg
                                                    sm={6}
                                                    md
                                                    className="LeftGridItem"
                                                  >
                                                    <div className="accordionColumn" key={nextId()}>
                                                      <Typography
                                                        className="accordionHeading"
                                                        key={nextId()}
                                                      >
                                                        Order Total
                                                      </Typography>
                                                      <Typography
                                                        className="accordionHeadingValue"
                                                        key={nextId()}
                                                      >
                                                        {getCurrencyInUSD(order.total)}
                                                      </Typography>
                                                    </div>
                                                  </Grid>

                                                  <Grid
                                                    item
                                                    xs={12}
                                                    lg
                                                    sm={6}
                                                    md
                                                    className="LeftGridItem"
                                                  >
                                                    <div className="accordionColumn" key={nextId()}>
                                                      <Typography
                                                        className="accordionHeading"
                                                        key={nextId()}
                                                      >
                                                        {fields.OrderStatusText?.value}
                                                      </Typography>
                                                      <Typography
                                                        className="accordionHeadingValue"
                                                        key={nextId()}
                                                      >
                                                        {state?.sitecoreDictionaryData &&
                                                          state?.sitecoreDictionaryData[
                                                            order.status
                                                          ]}
                                                      </Typography>
                                                    </div>
                                                  </Grid>

                                                  {(order.isPartiallyReturned ||
                                                    order.isOrderReturned) && (
                                                    <Grid
                                                      item
                                                      xs={12}
                                                      lg
                                                      sm={6}
                                                      md
                                                      className="LeftGridItem"
                                                    >
                                                      <div
                                                        className="accordionColumn"
                                                        key={nextId()}
                                                      >
                                                        <Typography
                                                          className="accordionHeading"
                                                          key={nextId()}
                                                        >
                                                          {state?.sitecoreDictionaryData
                                                            ?.RefundStatus || 'Refund Status'}
                                                        </Typography>
                                                        <Typography
                                                          className="accordionHeadingValue"
                                                          key={nextId()}
                                                        >
                                                          {order.isPartiallyReturned
                                                            ? state?.sitecoreDictionaryData
                                                                ?.PartialRefund || 'Partial Refund'
                                                            : order.isOrderReturned
                                                            ? state?.sitecoreDictionaryData
                                                                ?.FullRefund || 'Full Refund'
                                                            : ''}
                                                        </Typography>
                                                      </div>
                                                    </Grid>
                                                  )}

                                                  {order.shippingReturnId &&
                                                    order.shippingReturnId.length > 0 && (
                                                      <Grid
                                                        item
                                                        xs={12}
                                                        lg
                                                        sm={6}
                                                        md
                                                        className="LeftGridItem"
                                                      >
                                                        <div
                                                          className="accordionColumn"
                                                          key={nextId()}
                                                        >
                                                          <Typography
                                                            className="accordionHeading"
                                                            key={nextId()}
                                                          >
                                                            {state?.sitecoreDictionaryData
                                                              ?.ShippingRefundedDate ||
                                                              'Shipping Refunded Date'}
                                                          </Typography>
                                                          <Typography
                                                            className="accordionHeadingValue"
                                                            key={nextId()}
                                                          >
                                                            {convertDate(order.shippingRefunded)}
                                                          </Typography>
                                                        </div>
                                                      </Grid>
                                                    )}

                                                  {order.taxReturnId &&
                                                    order.taxReturnId.length > 0 && (
                                                      <Grid
                                                        item
                                                        xs={12}
                                                        lg
                                                        sm={6}
                                                        md
                                                        className="LeftGridItem"
                                                      >
                                                        <div
                                                          className="accordionColumn"
                                                          key={nextId()}
                                                        >
                                                          <Typography
                                                            className="accordionHeading"
                                                            key={nextId()}
                                                          >
                                                            {state?.sitecoreDictionaryData
                                                              ?.TaxRefundedDate ||
                                                              'Tax Refunded Date'}
                                                          </Typography>
                                                          <Typography
                                                            className="accordionHeadingValue"
                                                            key={nextId()}
                                                          >
                                                            {convertDate(order.taxRefunded)}
                                                          </Typography>
                                                        </div>
                                                      </Grid>
                                                    )}

                                                  {order.miscellaneousReturnItems &&
                                                    order.miscellaneousReturnItems.length > 0 && (
                                                      <Grid
                                                        item
                                                        xs={12}
                                                        lg
                                                        sm={6}
                                                        md
                                                        className="LeftGridItem"
                                                      >
                                                        <div
                                                          className="accordionColumn"
                                                          key={nextId()}
                                                        >
                                                          <Typography
                                                            className="accordionHeading"
                                                            key={nextId()}
                                                          >
                                                            {state?.sitecoreDictionaryData
                                                              ?.LastMiscRefund ||
                                                              'Last Misc. Refund'}
                                                          </Typography>
                                                          <Typography
                                                            className="accordionHeadingValue"
                                                            key={nextId()}
                                                          >
                                                            {convertDate(
                                                              order.miscellaneousReturnItems[0]
                                                                .dateRefunded
                                                            )}
                                                          </Typography>
                                                        </div>
                                                      </Grid>
                                                    )}

                                                  {order.isOrderReturned && (
                                                    <Grid
                                                      item
                                                      xs={12}
                                                      lg
                                                      sm={6}
                                                      md={4}
                                                      className="LeftGridItem"
                                                    >
                                                      <div
                                                        className="accordionColumn"
                                                        key={nextId()}
                                                      >
                                                        <Typography
                                                          className="accordionHeading"
                                                          key={nextId()}
                                                        >
                                                          {fields.DateRefundHeader?.value}
                                                        </Typography>
                                                        <Typography
                                                          className="accordionHeadingValue"
                                                          key={nextId()}
                                                        >
                                                          {convertDate(order.dateRefunded)}
                                                        </Typography>
                                                      </div>
                                                    </Grid>
                                                  )}
                                                </Grid>

                                                <Grid
                                                  item
                                                  xs={12}
                                                  lg={4}
                                                  className="OrderHeaderRightContainer"
                                                >
                                                  <div className="receipt-link">
                                                    {showReceipts(order)}
                                                  </div>
                                                </Grid>
                                              </Grid>
                                            </div>

                                            {order.orderItems.map(
                                              (orderDetails: IOrderItemDetails) => (
                                                <div key={nextId()} className="line-item">
                                                  <div className="line-item-details">
                                                    <Grid
                                                      item
                                                      xs={1}
                                                      sm={1}
                                                      md={1}
                                                      lg={1}
                                                      key={nextId()}
                                                      className={'order-quantity'}
                                                    >
                                                      <Typography
                                                        className="quantity-header"
                                                        key={nextId()}
                                                      >
                                                        Qty
                                                      </Typography>
                                                      <Typography
                                                        className="quantity-value"
                                                        key={nextId()}
                                                      >
                                                        {orderDetails?.quantity}
                                                      </Typography>
                                                    </Grid>
                                                    <Grid
                                                      container
                                                      className={'orderDetailsGrid'}
                                                      key={nextId()}
                                                    >
                                                      <Grid
                                                        item
                                                        xs={10}
                                                        sm={6}
                                                        md={6}
                                                        lg={8}
                                                        sx={{
                                                          px: 2,
                                                          py: 1,
                                                        }}
                                                        key={nextId()}
                                                      >
                                                        <div
                                                          className="accordionColumn accordionBodySegment"
                                                          key={nextId()}
                                                        >
                                                          {orderDetails?.product &&
                                                            (buyerOrder.lob ===
                                                              STANDARDS_IDENTIFIER ||
                                                            buyerOrder.lob ===
                                                              TRAINING_IDENTIFIER ? (
                                                              <Link
                                                                to={getProductUrl(
                                                                  orderDetails.product ?? null
                                                                )}
                                                                id={`${
                                                                  orderDetails?.product
                                                                    ?.productId || ''
                                                                }${
                                                                  orderDetails?.product?.edition &&
                                                                  orderDetails?.product?.edition
                                                                }${
                                                                  orderDetails?.product?.year &&
                                                                  orderDetails?.product?.year
                                                                }${
                                                                  orderDetails?.product?.term &&
                                                                  orderDetails?.product?.term
                                                                }`}
                                                                className="productTitle"
                                                                key={nextId()}
                                                              >
                                                                {orderDetails?.product?.title}
                                                                {addEllipsis(
                                                                  orderDetails?.product?.title || ''
                                                                )}
                                                              </Link>
                                                            ) : (
                                                              <Typography
                                                                className="productTitle"
                                                                key={nextId()}
                                                              >
                                                                {orderDetails?.product?.productNbr}
                                                              </Typography>
                                                            ))}
                                                          <div className="orderProductInfo">
                                                            {order.lob === ICP_IDENTIFIER &&
                                                              orderDetails?.product?.title && (
                                                                <Typography
                                                                  className="product-header product-header-desc"
                                                                  key={nextId()}
                                                                >
                                                                  <span className="product-value product-header-desc">
                                                                    {orderDetails?.product?.title}
                                                                    {addEllipsis(
                                                                      orderDetails?.product
                                                                        ?.title || ''
                                                                    )}{' '}
                                                                    {orderDetails?.product
                                                                      ?.examDate && (
                                                                      <span>
                                                                        {' '}
                                                                        -{' '}
                                                                        {
                                                                          orderDetails?.product
                                                                            ?.examDate
                                                                        }
                                                                      </span>
                                                                    )}
                                                                  </span>
                                                                </Typography>
                                                              )}

                                                            {orderDetails?.language && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                Language:{' '}
                                                                <span className="product-value">
                                                                  {orderDetails?.language}
                                                                </span>
                                                              </Typography>
                                                            )}
                                                            {orderDetails?.format && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                Format:{' '}
                                                                <span className="product-value">
                                                                  {orderDetails?.format}
                                                                </span>
                                                              </Typography>
                                                            )}
                                                            {orderDetails?.product
                                                              ?.rikOrderNumber && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                Order #:{' '}
                                                                <span className="product-value">
                                                                  {
                                                                    orderDetails?.product
                                                                      ?.rikOrderNumber
                                                                  }
                                                                </span>
                                                              </Typography>
                                                            )}

                                                            {order.lob === TRAINING_IDENTIFIER &&
                                                              typeof orderDetails?.product
                                                                ?.edition === 'number' &&
                                                              orderDetails.product.edition > 0 && (
                                                                <Typography
                                                                  className="product-header"
                                                                  key={nextId()}
                                                                >
                                                                  Edition:{' '}
                                                                  <span className="product-value">
                                                                    {orderDetails.product.edition}
                                                                  </span>
                                                                </Typography>
                                                              )}

                                                            {order.lob === TRAINING_IDENTIFIER &&
                                                              orderDetails?.product?.productId && (
                                                                <Typography
                                                                  className="product-header"
                                                                  key={nextId()}
                                                                >
                                                                  Product Id:{' '}
                                                                  <span className="product-value">
                                                                    {
                                                                      orderDetails?.product
                                                                        ?.productId
                                                                    }
                                                                  </span>
                                                                </Typography>
                                                              )}

                                                            {orderDetails?.product?.modality && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                Modality:{' '}
                                                                <span className="product-value">
                                                                  {
                                                                    MODALITY_TITLES[
                                                                      orderDetails?.product
                                                                        ?.modality
                                                                    ]
                                                                  }
                                                                </span>
                                                              </Typography>
                                                            )}

                                                            {orderDetails?.sessionStartDate && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                Start Date:{' '}
                                                                <span className="product-value">
                                                                  {
                                                                    getDateTimePartsFromIso(
                                                                      orderDetails.sessionStartDate
                                                                    )?.date
                                                                  }
                                                                </span>
                                                              </Typography>
                                                            )}

                                                            {orderDetails?.sessionEndDate && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                End Date:{' '}
                                                                <span className="product-value">
                                                                  {
                                                                    getDateTimePartsFromIso(
                                                                      orderDetails.sessionEndDate
                                                                    )?.date
                                                                  }
                                                                </span>
                                                              </Typography>
                                                            )}
                                                            {orderDetails?.sessionDescription && (
                                                              <Typography
                                                                className="product-header"
                                                                key={nextId()}
                                                              >
                                                                Description:{' '}
                                                                <span
                                                                  className="product-value session-desc"
                                                                  dangerouslySetInnerHTML={{
                                                                    __html: DOMPurify.sanitize(
                                                                      orderDetails.sessionDescription ||
                                                                        ''
                                                                    ),
                                                                  }}
                                                                />
                                                              </Typography>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </Grid>
                                                      <Grid
                                                        item
                                                        xs={10}
                                                        sm={5}
                                                        md={5}
                                                        lg={4}
                                                        className="orderDetailsActions"
                                                        key={nextId()}
                                                      >
                                                        <AccordionActions
                                                          className="buttonActionSection"
                                                          key={nextId()}
                                                        >
                                                          {order.lob === STANDARDS_IDENTIFIER &&
                                                            showManageAssignmentsButton(
                                                              unifiedOrder.unifiedBuyerOrders[0]
                                                                .buyerOrders[0],
                                                              orderDetails
                                                            )}

                                                          {order.lob === STANDARDS_IDENTIFIER &&
                                                            orderDetails?.product
                                                              ?.isAmendmentExist &&
                                                            !unifiedOrder.unifiedBuyerOrders[0]
                                                              .buyerOrders[0].isOrderReturned &&
                                                            unifiedOrder.unifiedBuyerOrders[0]
                                                              .buyerOrders[0].paymentProcessed && (
                                                              <Link
                                                                to={
                                                                  getProductUrl(
                                                                    orderDetails.product ?? null
                                                                  ) + '#addenda'
                                                                }
                                                                className="buttonBorderBlue"
                                                                id={`${
                                                                  orderDetails?.product
                                                                    ?.productId || ''
                                                                }${
                                                                  orderDetails?.product?.edition &&
                                                                  orderDetails?.product?.edition
                                                                }`}
                                                              >
                                                                <FaFileAlt key={nextId()} />
                                                                {fields?.AddendaErrataText.value}
                                                              </Link>
                                                            )}

                                                          {order.lob === STANDARDS_IDENTIFIER &&
                                                            !orderDetails?.product?.isRecent &&
                                                            !unifiedOrder.unifiedBuyerOrders[0]
                                                              .buyerOrders[0].isOrderReturned &&
                                                            orderDetails?.product
                                                              ?.isStatisticsReport === false && (
                                                              <Link
                                                                to={getProductUrl(
                                                                  orderDetails.product ?? null
                                                                )}
                                                                id={`${
                                                                  orderDetails?.product
                                                                    ?.productId || ''
                                                                }${
                                                                  orderDetails?.product?.edition &&
                                                                  orderDetails?.product?.edition
                                                                }`}
                                                                className="buttonBorderBlue"
                                                                key={nextId()}
                                                              >
                                                                <FaFileMedical key={nextId()} />
                                                                {fields?.NewEditionText.value}
                                                              </Link>
                                                            )}

                                                          {showTrackPackageButton(
                                                            unifiedOrder.unifiedBuyerOrders[0]
                                                              .buyerOrders[0],
                                                            orderDetails
                                                          )}

                                                          {orderDetails?.statisticsApplied ===
                                                            true &&
                                                            unifiedOrder.unifiedBuyerOrders[0]
                                                              .buyerOrders[0]?.isRIKOrderCreated ===
                                                              true &&
                                                            (unifiedOrder.unifiedBuyerOrders[0]
                                                              .buyerOrders[0]?.paymentProcessed ===
                                                              true ||
                                                              unifiedOrder.unifiedBuyerOrders[0]
                                                                .buyerOrders[0]?.isNoCostOrder ===
                                                                true) && (
                                                              <Link
                                                                to="/statistics-store"
                                                                id={`${
                                                                  orderDetails?.product
                                                                    ?.productId || ''
                                                                }${
                                                                  orderDetails?.product?.year &&
                                                                  orderDetails?.product?.year
                                                                }${
                                                                  orderDetails?.product?.term &&
                                                                  orderDetails?.product?.term
                                                                } `}
                                                                className="buttonBorderBlue"
                                                                key={nextId()}
                                                              >
                                                                {
                                                                  fields?.GoToStatisticsStoreText
                                                                    .value
                                                                }
                                                              </Link>
                                                            )}

                                                          {order.lob === ICP_IDENTIFIER &&
                                                            fields?.ICPRedirectionLink?.value
                                                              ?.href &&
                                                            orderDetails?.product?.accountId &&
                                                            orderDetails?.product
                                                              .applicationNumber && (
                                                              <a
                                                                href={fields.ICPRedirectionLink.value.href
                                                                  .replace(
                                                                    '##accountId##',
                                                                    orderDetails.product.accountId
                                                                  )
                                                                  .replace(
                                                                    '##applicationNumber##',
                                                                    orderDetails.product
                                                                      .applicationNumber
                                                                  )}
                                                                target={
                                                                  fields.ICPRedirectionLink.value
                                                                    .target
                                                                }
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                  textDecoration: 'none',
                                                                  width: '100%',
                                                                }}
                                                              >
                                                                <Button
                                                                  color="secondary"
                                                                  variant="contained"
                                                                  className="buttonFillBlue"
                                                                >
                                                                  {
                                                                    fields.ICPRedirectionLink.value
                                                                      .text
                                                                  }
                                                                </Button>
                                                              </a>
                                                            )}

                                                          {order.paymentProcessed &&
                                                            order.lob === TRAINING_IDENTIFIER &&
                                                            orderDetails.isItemFulfilled !==
                                                              false && (
                                                              <EnrollmentKeysSection
                                                                productId={
                                                                  orderDetails?.product?.productId
                                                                }
                                                                orderId={order.orderId}
                                                                quantity={orderDetails.quantity}
                                                                enrollmentKey={
                                                                  orderDetails.enrollmentKey
                                                                }
                                                                trainingRedirectionLink={
                                                                  fields?.TrainingRedirectionLink
                                                                }
                                                                learnUponRedirectionLink={
                                                                  fields?.LearnUponRedirectionLink
                                                                }
                                                                TooltipText={
                                                                  fields.EnrollmentKeyTooltip
                                                                }
                                                                isLoading={
                                                                  !orderDetails.isItemFulfilled
                                                                }
                                                              />
                                                            )}
                                                        </AccordionActions>
                                                      </Grid>
                                                      {unifiedOrder.unifiedBuyerOrders.map(
                                                        (buyerOrder) =>
                                                          buyerOrder.buyerOrders.map(
                                                            (order) =>
                                                              order.isPartiallyReturned &&
                                                              orderDetails.returnItems &&
                                                              orderDetails.returnItems.length > 0 &&
                                                              getPartialRefundStatusForLineItem(
                                                                orderDetails.returnItems,
                                                                orderDetails.quantity
                                                              )
                                                          )
                                                      )}
                                                    </Grid>
                                                  </div>{' '}
                                                  {order.lob === TRAINING_IDENTIFIER &&
                                                    order.paymentProcessed &&
                                                    orderDetails.isItemFulfilled === false && (
                                                      <div
                                                        className="enrollment-error"
                                                        key={nextId()}
                                                      >
                                                        <p>
                                                          {orderDetails?.quantity &&
                                                          orderDetails.quantity > 1
                                                            ? fields?.KeyGenerationFailureMessage
                                                                ?.value
                                                            : fields?.CourseEnrollmentFailureMessage
                                                                ?.value}
                                                        </p>
                                                      </div>
                                                    )}
                                                </div>
                                              )
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )
                                )}
                              </>
                            ) : (
                              unifiedOrder.unifiedBuyerOrders.map(
                                (buyerOrder: IUnifiedBuyerOrder) => (
                                  <div key={nextId()}>
                                    {buyerOrder.buyerOrders.map(
                                      (order: IBuyerOrder) =>
                                        order.orderItems.length > 0 && (
                                          <div key={nextId()}>
                                            {order.orderItems.map(
                                              (orderDetails: IOrderItemDetails) => (
                                                <div key={nextId()} className="line-item-details">
                                                  <Grid
                                                    item
                                                    xs={1}
                                                    sm={1}
                                                    md={1}
                                                    lg={1}
                                                    className="order-quantity"
                                                  >
                                                    <Typography className="quantity-header">
                                                      Qty
                                                    </Typography>
                                                    <Typography className="quantity-value">
                                                      {orderDetails?.quantity}
                                                    </Typography>
                                                  </Grid>
                                                  <Grid container className="orderDetailsGrid">
                                                    <Grid item xs={10} sm={6} md={6}>
                                                      <div className="accordionColumn accordionBodySegment">
                                                        <Link
                                                          to={getProductUrl(
                                                            orderDetails.product ?? null
                                                          )}
                                                          id={`${
                                                            orderDetails?.product?.productId || ''
                                                          }${
                                                            orderDetails?.product?.edition
                                                              ? orderDetails?.product?.edition
                                                              : ''
                                                          }${
                                                            orderDetails?.product?.year
                                                              ? orderDetails?.product?.year
                                                              : ''
                                                          }${
                                                            orderDetails?.product?.term
                                                              ? orderDetails?.product?.term
                                                              : ''
                                                          }`}
                                                          className="productTitle"
                                                          key={nextId()}
                                                        >
                                                          {orderDetails?.product?.title}
                                                          {addEllipsis(
                                                            orderDetails?.product?.title || ''
                                                          )}
                                                        </Link>

                                                        <div className="orderProductInfo">
                                                          <Typography className="product-header">
                                                            Product Code:{' '}
                                                            <span className="product-value">
                                                              {orderDetails?.product?.productNbr}
                                                            </span>
                                                          </Typography>
                                                          <Typography className="product-header">
                                                            Language:{' '}
                                                            <span className="product-value">
                                                              {orderDetails?.language}
                                                            </span>
                                                          </Typography>
                                                          <Typography className="product-header">
                                                            Format:{' '}
                                                            <span className="product-value">
                                                              {orderDetails?.format}
                                                            </span>
                                                          </Typography>
                                                        </div>
                                                      </div>
                                                    </Grid>
                                                    <Grid
                                                      item
                                                      xs={10}
                                                      sm={5}
                                                      md={5}
                                                      lg={4}
                                                      className="orderDetailsActions"
                                                    >
                                                      <AccordionActions className="buttonActionSection" />
                                                    </Grid>
                                                  </Grid>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        )
                                    )}
                                  </div>
                                )
                              )
                            )}
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )
                );
              })
            ) : (
              <>
                <span className="no-results-text">No Results Found!</span>
              </>
            )}
          </div>
        </>
      )}
      <ManageAssignments
        props={{
          open: openManageAssignmentsPopup,
          closeManageAssignmentsPopup: onCloseManageAssignmentsPopup,
          StatusTooltipText: fields.StatusTooltipText,
          ActionTooltipText: fields.ActionTooltipText,
          ResendButtonText: fields.ResendButtonText,
          ReassignButtonText: fields.ReassignButtonText,
          CancelButtonText: fields.CancelButtonText,
          SaveAndSendLink: fields.SaveAndSendLink,
          EmailErrorMessage: fields.EmailErrorMessage,
          EmailSentHeaderText: fields.EmailSentHeaderText,
          EmailHeaderText: fields.EmailHeaderText,
          ActionsHeaderText: fields.ActionsHeaderText,
          orderId: orderId,
          lineItemId: lineItemId,
        }}
      ></ManageAssignments>
    </div>
  );
};

export default OrderHistory;
